import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { ErrorHandlerService } from '@y42/error-handling';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams, Optional } from 'ag-grid-community';
import { BehaviorSubject } from 'rxjs';
import { JobDialogService } from '../../job-dialog/job-dialog.service';

type SortKey = null | 'asc' | 'desc';
@UntilDestroy()
@Component({
  selector: 'y42-job-status-header',
  templateUrl: './job-status-header-renderer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JobDialogService],
})
export class JobStatusHeaderRendererComponent implements IHeaderAngularComp, OnDestroy {
  constructor(
    errorHanderService: ErrorHandlerService,
    // Don't remove the Optional decorator or it's going to break the app in multiple places
    @Optional() protected readonly jobDialogService?: JobDialogService | null,
  ) {
    if (!this.jobDialogService) {
      errorHanderService.log('[JobStatusHeaderRendererComponent.openJobsDialog]: JobDialogService is not provided');
    }
  }
  @HostBinding('class') class = 'ag-cell-label-container';

  params!: IHeaderParams & { hideAllJobsButton: boolean };
  private readonly filterActive$ = new BehaviorSubject<boolean>(false);
  private readonly sortedIcon$ = new BehaviorSubject<IconProp | null>(null);
  sorted: SortKey = null;
  @ViewChild('menuButton', { read: ElementRef }) public menuButton?: ElementRef;

  public readonly state = connectState(this, {
    filterActive: this.filterActive$,
    sortedIcon: this.sortedIcon$,
  });
  sortChangeBind = this.onSortChanged.bind(this);
  filterChangeBind = this.onFilterChanged.bind(this);

  agInit(params: this['params']): void {
    this.params = params;
    this.params.column.addEventListener('sortChanged', this.sortChangeBind);
    this.params.column.addEventListener('filterChanged', this.filterChangeBind);
    this.filterActive$.next(this.params.column.isFilterActive());
  }

  ngOnDestroy(): void {
    this.params.column.removeEventListener('sortChanged', this.sortChangeBind);
    this.params.column.removeEventListener('filterChanged', this.filterChangeBind);
  }

  onMenuClicked() {
    if (this.menuButton) {
      this.params.showColumnMenu(this.menuButton.nativeElement);
    }
  }

  onSortChanged() {
    this.sorted = null;
    if (this.params.column.isSortAscending()) {
      this.sorted = 'asc';
    } else if (this.params.column.isSortDescending()) {
      this.sorted = 'desc';
    }
    this.sortedIcon$.next(this.sortedIcon);
  }

  get sortedIcon(): IconProp | null {
    let icons: IconProp | null = null;
    if (this.sorted === 'desc') {
      icons = ['fal', 'arrow-down'];
    } else if (this.sorted === 'asc') {
      icons = ['fal', 'arrow-up'];
    }
    return icons;
  }

  onSortRequested(sorted: SortKey) {
    this.params.setSort(this.sortSwitch(sorted));
  }

  sortSwitch(sort: SortKey) {
    if (sort === null) {
      return 'asc';
    } else if (sort === 'asc') {
      return 'desc';
    } else {
      return null;
    }
  }

  onFilterChanged() {
    this.filterActive$.next(this.params.column.isFilterActive());
  }

  refresh(): boolean {
    return false;
  }

  openJobsDialog() {
    this.jobDialogService?.open({
      data: {
        parentId: undefined,
        featureType: undefined,
      },
    });
  }
}
